import {UnreachableCaseError} from 'ts-essentials';
import {useEffect, useState, MutableRefObject} from 'react';
import {clampToScreen} from './clamp-to-screen';
import {ElementPosition, getElementPosition, ZERO_ELEMENT_POSITION} from './get-element-position';
import {useRecalculate} from './recalculate';
import {scaleElement} from './scale-element';
import {translatePosition} from './translate-position';

type Anchor
  = 'Bottom'
    | 'Right'
  ;

function getTranslateX(anchor: Anchor, pos: ElementPosition): number {
  switch (anchor) {
    case 'Bottom': {
      return 0;
    }

    case 'Right': {
      return pos.width;
    }

    default: {
      throw new UnreachableCaseError(anchor);
    }
  }
}

function getTranslateY(anchor: Anchor, pos: ElementPosition): number {
  switch (anchor) {
    case 'Right': {
      return 0;
    }

    case 'Bottom': {
      return pos.height;
    }

    default: {
      throw new UnreachableCaseError(anchor);
    }
  }
}

export function usePositionOverlay<T extends HTMLElement, U extends HTMLElement>(
  target: MutableRefObject<T | null>,
  overlay: MutableRefObject<U | null>,
  anchor: Anchor = 'Bottom',
): [ElementPosition, () => void] {
  const [overlayPos, setOverlayPos] = useState(ZERO_ELEMENT_POSITION);
  const recalculate = useRecalculate();

  useEffect(() => {
    if ((target.current != null) && (overlay.current != null)) {
      const listPos = getElementPosition(overlay.current);
      let pos = getElementPosition(target.current);
      pos = translatePosition(pos, getTranslateX(anchor, pos), getTranslateY(anchor, pos));
      pos = scaleElement(pos, listPos.width, listPos.height);
      pos = clampToScreen(pos);
      setOverlayPos(pos);
    }
  }, [anchor, target, overlay, recalculate]);

  return [overlayPos, recalculate];
}
